import React, { useState, useEffect, useRef } from "react";
import { FaPlus, FaUserFriends } from "react-icons/fa"; // Import the collaboration icon
import "./FlowCard.css"; // Import the CSS file for styling
import { useNavigate } from "react-router-dom";
import placeholder from "../assets/focusmap-placeholder.png";
import { FaEllipsisV } from "react-icons/fa";
import "primeicons/primeicons.css";
import {
  doc,
  updateDoc,
  deleteDoc,
  DocumentData,
  DocumentReference,
} from "firebase/firestore";
import { db, getImageFromStorage } from "../firebase";
import ConfirmModal from "./ConfirmModal"; // Import the ConfirmModal component
import { Flow } from "../types/types";
import "primeicons/primeicons.css";
import { deleteFlowData } from "../storage/storageProxy";
import { useStore } from "../App/store";
import { formatTimestamp } from "../Helpers/Calculations";
import { truncateSync } from "node:fs";

interface FlowCardProps {
  flow: Flow;
  handleFlowClick: (id: string) => void;
  reSortData: (id: string, isFavourite: boolean) => void;
  loadFlows: () => void;
}

const FlowCard: React.FC<FlowCardProps> = ({
  flow,
  handleFlowClick,
  reSortData,
  loadFlows,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showRenameConfirmModal, setShowRenameConfirmModal] = useState(false);
  const [showRenameInput, setShowRenameInput] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);
  const [flowName, setFlowName] = useState(flow.name);

  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLDivElement>,
    action: string
  ) => {
    event.stopPropagation();
    if (action === "rename") {
      setShowRenameConfirmModal(true);
    } else if (action === "delete") {
      setShowDeleteConfirmModal(true);
    } else {
      alert("Not yet available");
    }
    setShowMenu(false);
  };

  const handleConfirmDelete = async (event: React.MouseEvent) => {
    try {
      await deleteFlowData(flow.id, useStore.getState().useLocalStorage);
    } catch (error) {
      console.error("Error deleting document: ", error);
      alert("Failed to delete document");
    }
    event.stopPropagation();
    setShowDeleteConfirmModal(false);
    loadFlows();
  };

  const handleCancelDelete = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDeleteConfirmModal(false);
  };

  const handleConfirmRename = async (
    event: React.MouseEvent,
    renamed: string
  ) => {
    event.stopPropagation();
    try {
      await updateDoc(doc(db, "flows", flow.id), { name: renamed });
      setFlowName(renamed);
    } catch (error) {
      console.error("Error renaming document: ", error);
      alert("Failed to rename document");
    }
    setShowRenameConfirmModal(false);
  };

  const handleCancelRename = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowRenameConfirmModal(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    setIsFavourite(flow.favourite);
  }, []);

  useEffect(() => {
    const fetchImage = async () => {
      const storedImage = await getImageFromStorage(`${flow.id}.png`);
      if (storedImage) {
        setImageSrc(storedImage);
      } else {
        setImageSrc(placeholder);
      }
    };

    fetchImage();
  }, [flow.id]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const makeFavourite = async (
    event: React.MouseEvent<HTMLElement>,
    isFavourite: boolean
  ) => {
    event.stopPropagation();
    try {
      const flowRef = doc(db, "flows", flow.id);
      await updateDoc(flowRef, { favourite: isFavourite });
      setIsFavourite((preValue) => !preValue);
      reSortData(flow.id, isFavourite);
    } catch (error) {
      console.error("Error updating favourite status: ", error);
      alert("Failed to update favourite status");
    }
  };

  return (
    <div
      key={flow.id}
      className="flow-card"
      onClick={() => handleFlowClick(flow.id)}
    >
      <div className="flow-card-image-container">
        {isFavourite ? (
          <i
            className="pi pi-heart-fill favourite"
            onClick={(event) => makeFavourite(event, false)}
          ></i>
        ) : (
          <i
            className="pi pi-heart not-favourite"
            onClick={(event) => makeFavourite(event, true)}
          ></i>
        )}
        <img
          src={imageSrc || placeholder}
          alt="Map Placeholder"
          className="flow-card-image"
        />
        {flow.role === "collaborator" && (
          <FaUserFriends className="collaborator-icon" color="#3c5973" />
        )}
      </div>
      <div className="flow-card-details">
        <h2 className="h2">{flowName}</h2>
        <div className="flow-card-menu" onClick={handleMenuClick} ref={menuRef}>
          <FaEllipsisV
            style={{
              fontSize: "1rem",
              color: "white",
              width: "20px",
              height: "20px",
            }}
          />
          {showMenu && (
            <div className="popup-menu">
              <div
                className="popup-menu-item"
                onClick={(event) => handleMenuItemClick(event, "rename")}
              >
                Rename
              </div>
              <div
                className="popup-menu-item"
                onClick={(event) => handleMenuItemClick(event, "delete")}
              >
                Delete
              </div>
            </div>
          )}
        </div>
        <p>Last saved {formatTimestamp(flow.timestamp)}</p>
      </div>
      {showDeleteConfirmModal && (
        <ConfirmModal
          message="Are you sure you want to delete this map?"
          inputText={null}
          inputDescription={null}
          onConfirm={(event: React.MouseEvent) => handleConfirmDelete(event)}
          onCancel={(event: React.MouseEvent) => handleCancelDelete(event)}
        />
      )}
      {showRenameConfirmModal && (
        <ConfirmModal
          message="Are you sure you want to rename this map?"
          inputText={flowName}
          inputDescription={null}
          onConfirm={(event: React.MouseEvent, renamed: string) =>
            handleConfirmRename(event, renamed)
          }
          onCancel={(event: React.MouseEvent) => handleCancelRename(event)}
        />
      )}
    </div>
  );
};

export default FlowCard;
